import React from "react";
import {SubmitHandler, useForm} from "react-hook-form";
import {z} from "zod";
import {zodResolver} from "@hookform/resolvers/zod";

import {TextField} from "@components/Form/TextField";
import {TextareaField} from "@components/Form/TextareaField";
import {SelectField} from "@components/Form/SelectField";
import {PrivacyField} from "@components/Form/PrivacyField";

const REQUIRED = "This field is required.";

const schema = z.object({
    email: z.string().min(1, REQUIRED).email("Invalid email"),
    name: z.string().min(1, REQUIRED),
    phone: z.string().min(1, REQUIRED),
    vat: z.string().min(8, REQUIRED),

    about: z.string().min(1, REQUIRED),
    country: z.string().min(1, REQUIRED),

    gdpr1: z.literal("true", {error: REQUIRED}),
    gdpr2: z.enum(["true", "false"]),
    gdpr3: z.enum(["true", "false"]),
});

type FormValues = z.infer<typeof schema>;

export default function FormTestPage() {
    const {
        control,
        handleSubmit,
        formState: {errors, isSubmitting, isValid},
    } = useForm<FormValues>({
        resolver: zodResolver(schema),
        defaultValues: {
            email: "",
            name: "",
            phone: "",
            vat: "",
            about: "",
            country: "",
            gdpr1: undefined as any,
            gdpr2: undefined as any,
            gdpr3: undefined as any,
        },
        mode: "onChange",
    });

    const onSubmit: SubmitHandler<FormValues> = async (data) => {
        console.log("FORM SUBMIT", data);
        alert("Check console for submitted payload");
    };

    return (
        <div className="page">
            <div className="page__content">
                <div style={{maxWidth: 720, margin: "0 auto"}}>
                    <h1 className="tp-l">Form Components Test</h1>
                    <p className="tp-s" style={{marginTop: 8, marginBottom: 24, color: "#475467"}}>
                        Questa pagina renderizza tutti i componenti form (TextField, VatTextField, SelectField,
                        TextareaField,
                        PrivacyField).
                    </p>

                    <form className="form" onSubmit={handleSubmit(onSubmit)}>
                        <TextField
                            control={control}
                            name="name"
                            label="Name *"
                            type="text"
                            error={errors.name?.message}
                            disabled={isSubmitting}
                            showValidatedIcon={false}
                        />

                        <TextField
                            control={control}
                            name="email"
                            label="Email *"
                            type="email"
                            error={errors.email?.message}
                            disabled={isSubmitting}
                            showValidatedIcon={false}
                        />

                        <TextField
                            control={control}
                            name="phone"
                            label="Phone *"
                            type="tel"
                            error={errors.phone?.message}
                            disabled={isSubmitting}
                            showValidatedIcon={false}
                        />

                        <TextField
                            control={control}
                            name="vat"
                            label="VAT number *"
                            disabled={isSubmitting}
                            error={errors.vat?.message}
                        />

                        <SelectField
                            control={control}
                            name="country"
                            label="Country *"
                            disabled={isSubmitting}
                            error={errors.country?.message}
                            placeholder="Select country..."
                            options={[
                                {value: "IT", label: "Italy"},
                                {value: "FR", label: "France"},
                                {value: "DE", label: "Germany"},
                                {value: "ES", label: "Spain"},
                            ]}
                        />

                        <TextareaField
                            control={control}
                            name="about"
                            label="About you *"
                            disabled={isSubmitting}
                            error={errors.about?.message}
                            rows={5}
                        />

                        <div style={{marginTop: 10}}/>

                        <PrivacyField
                            control={control}
                            name="gdpr1"
                            disabled={isSubmitting}
                            error={errors.gdpr1?.message}
                            title="Privacy policy"
                            text="gdpr1 must be TRUE (AGREE). If you select DO NOT AGREE you must see an error."
                        />

                        <PrivacyField
                            control={control}
                            name="gdpr2"
                            disabled={isSubmitting}
                            error={errors.gdpr2?.message}
                            title="Marketing"
                            text="Optional consent: true/false both valid."
                        />

                        <PrivacyField
                            control={control}
                            name="gdpr3"
                            disabled={isSubmitting}
                            error={errors.gdpr3?.message}
                            title="Third parties"
                            text="Optional consent: true/false both valid."
                            withDivider={false}
                        />

                        <button
                            type="submit"
                            disabled={isSubmitting || !isValid}
                            className="cta cta--medium"
                            style={{marginTop: 16}}
                        >
                            <span className="label">Submit test</span>
                            <span className="icon">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 6 11">
                  <path
                      fill="#fff"
                      d="M.1464.1464a.5.5 0 0 1 .707 0l4.6667 4.6667a.4999.4999 0 0 1 0 .707L.8535 10.1868a.4999.4999 0 1 1-.707-.707l4.3131-4.3132L.1464.8535a.5.5 0 0 1 0-.707Z"
                  />
                </svg>
              </span>
                        </button>

                        <div style={{marginTop: 12, fontSize: 12, color: "#667085"}}>
                            <strong>Debug:</strong> isValid = {String(isValid)} — submitting = {String(isSubmitting)}
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}
